import React, {useEffect, useState, useRef} from 'react';
import {Button, Col, Drawer, Image, Input, message, Modal, Row, Select, Skeleton} from 'antd';
import {useLocation} from 'react-router-dom';
import {getErrorKeys, showError} from '../../../../utils/exceptionUtils';
import {translateGraphQLError} from '../../../../utils/graphqlUtils';
import {CameraOutlined, CheckOutlined, CloseOutlined, DownOutlined, EditOutlined, EyeInvisibleOutlined, EyeOutlined, UpOutlined} from '@ant-design/icons';
import ImageUploader from '../../../../components/ImageUploader';
import {PODReviewStatus} from "../../../../enums/DeliveryEnum";
import {AccessControlType} from "../../../../enums/AccessControlEnum";
import {useLazyQuery, useMutation, useQuery} from "@apollo/client";
import {GET_ORDER_DETAIL_FOR_HISTORY, HistoryOrderDetailResponse} from "../graphql/queries/getOrderDetailForHistory";
import { GET_INT_ENUM_VALUES, GetIntEnumValuesResponse } from '../../../../graphql/queries/getEnumValues';
import {MARK_AS_EXCEPTION} from "../../../../graphql/mutations/markAsException";
import {BATCH_MARK_FOR_MANUAL_REVIEW, BatchMarkForManualReviewResponse} from "../graphql/mutations/batchMarkForManualReviewMutations";
import { POD_IMAGE_UPDATE, PodImageUpdateInput, PodImageUpdateResponse } from "../graphql/mutations/podImageUpdate";
import {formatTimestampToDateTime} from "../../../../utils/dateTimeUtils";
import { hasHeadquartersAccess, isCS, isKAQC, isCSFollowUp, hasActualDimensionEditPermission, AppRoleConstants } from "../../../../utils/auth";
import { hasResource } from "../../../../utils/resourceAuth";
import { ResourceCode } from "../../../../constants/resourceCodes";
import AddressInfoBlock, {
    AddressInfo,
    buildLocationPendingFromDetail,
    splicingAddress, splicingGeoInfo,
    splicingPhoneInfo
} from "../../../delivery/parcels/components/AddressInfoBlock";
import AddressGuideModal from "../../../delivery/parcels/components/AddressGuideModal";
import {DimensionInfo} from 'pages/delivery/parcels/components/ParcelDimensionInfoBlock';
import {CHANGE_ORDER_RECEIVER_ADDRESS} from 'pages/delivery/parcels/graphql/mutations/changeOrderReceiverAddress';
import {CHANGE_ORDER_RECIPIENT_CONTACT} from 'pages/delivery/parcels/graphql/mutations/changeOrderRecipientContact';
import {
    GENERATE_SHIPPING_LABEL_PDF,
    GenerateShippingLabelPdfResponse,
    GenerateShippingLabelPdfVariables
} from 'pages/delivery/parcels/graphql/mutations/generateShippingLabelPdf';
import {
    CentralOrderDetailResponse,
    GET_ORDER_DETAIL_FOR_CENTRAL
} from 'pages/delivery/parcels/graphql/queries/getOrderDetailForCentral';
import {
    ExceptionDictionaryByCodeResponse,
    GET_EXCEPTION_DICTIONARY_BY_REASON_CODE
} from "../../../delivery/parcels/graphql/queries/getExceptionDictionaryByReasonCode";
import {channelTypeResponse, GET_CHANNEL} from "../../../../graphql/queries/getChannel";
import MarkAsExceptionModal, {MarkAsExceptionData} from 'components/MarkAsExceptionModal';
import {printPdfFromBase64} from "../../../../utils/printUtils";
import ParcelDetailItem from 'pages/delivery/parcels/components/ParcelDetailItem';
import ParcelHistorySection from "../../../../components/ParcelHistorySection";
import {GET_ZONE} from "../../../../graphql/queries/getZone";
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import { SiteType } from 'graphql/types/SiteType';
import { PodReviewMessageItemType } from '../../../../graphql/types/OrderType';
import { getTranslatedPodReviewMessage } from '../../../../utils/podReviewMessageUtils';
import {formatWeightKgToLb, formatLengthCmToInch} from "../../../../utils/numberUtils";
import {resolveTemuDisplayFromOrder} from "../../../../utils/temuSortingCode";
import { useTranslation } from 'react-i18next';
import { useStatusInternationalization } from 'pages/delivery/parcels/hooks/useStatusInternationalization';
import OrderNoteEditor from '../../../../components/OrderNoteEditor';
import { getOrderNoteText } from '../../../../utils/orderNoteUtils';
import { useUpdateOrderNote } from '../../../../hooks/useUpdateOrderNote';
import ActualDimensionEditor, { ActualDimensionInfo } from "../../../delivery/parcels/components/ActualDimensionEditor";
import { formatStopNumber } from '../../../delivery/tasks/utils/stopNumberUtils';
import { UPDATE_ORDER_ACTUAL_DIMENSION, UpdateOrderActualDimensionInput } from "../../../delivery/parcels/graphql/mutations/updateOrderActualDimension";
import {
    GET_ACCESS_CODES_BY_ORDER_ID,
    GetAccessCodesByOrderIdResponse,
    GetAccessCodesByOrderIdVariables
} from "../graphql/queries/getAccessCodesByOrderId";
import {
    CREATE_ACCESS_CODE,
    UPDATE_ACCESS_CODE,
    DELETE_ACCESS_CODE,
    CreateAccessCodeResponse,
    CreateAccessCodeVariables,
    UpdateAccessCodeResponse,
    UpdateAccessCodeVariables,
    DeleteAccessCodeResponse,
    DeleteAccessCodeVariables,
    SAVE_PC_ACCESS_CODE_QUERY_LOG,
    SavePcAccessCodeQueryLogResponse,
    SavePcAccessCodeQueryLogVariables
} from "../graphql/mutations/accessCodeMutations";
import {
    REVOKE_DELIVERY,
    RevokeDeliveryVariables,
    RevokeDeliveryResponse
} from '../../../../graphql/mutations/revokeDelivery';
import {GET_DELIVERY_REVOKE_REASON_I18N_CONFIG, GetDeliveryRevokeReasonI18nConfigResponse} from "../../../../graphql/queries/getDeliveryRevokeReasonI18nConfig";
import { useSubmitOrderAddressLocationChange } from '../../../../hooks/useSubmitOrderAddressLocationChange';

interface CentralParcelDetailDrawerProps {
    visible: boolean;
    onClose: () => void;
    orderId: string;
    userRole: string;
    onRefreshList?: () => void;
    hideEditActions?: boolean;
    extraActionButtons?: React.ReactNode;
    hidePrintAndPodReview?: boolean;
    showExternalTrack?: boolean;  //是否展示外部轨迹切换（仅总部包裹页为 true）
    showInterceptionExemptionAlert?: boolean;  //是否展示原始地址异常提示（仅总部、WMS 入口为 true）
    /**
     * 历史包裹（已归档订单）模式。
     *
     * true 时主查询走 getOrderDetailForHistory（数据来自 order_history / order_event_history），
     * 其余 8 个辅助查询（枚举 / 异常字典 / 渠道 / zone / access code / 撤销原因）保持不变
     * ——它们查的是参考数据不是订单数据。
     *
     * 归档订单只读：后端不返回任何可编辑性标记，配合 hideEditActions 使用。
     */
    isHistory?: boolean;
    /**
     * 嵌入其他模块的纯只读模式（如罚单申诉详情查看包裹），配合 hideEditActions、hidePrintAndPodReview 使用。
     *
     * true 时隐藏 hideEditActions 未覆盖的门禁码编辑、妥投撤回、实际尺寸编辑入口；右侧包裹历史照常展示。
     */
    embeddedReadOnly?: boolean;
}

// POD评价接口
const submitPodReview = async (trackingNumber: string,
                               review: PODReviewStatus.MANUAL_DISAPPROVED | PODReviewStatus.MANUAL_APPROVED):
    Promise<boolean> => {
    try {
        return await new Promise(resolve => setTimeout(() => resolve(true), 500));
    } catch (error) {
        console.error('提交POD评价失败:', error);
        return false;
    }
};

// 格式化POD Review状态显示
const getPodReviewStatusText = (status: number, t: any) => {
    switch (status) {
        case PODReviewStatus.NOT_REVIEWED:
            return t('enum.podReviewStatus.NOT_REVIEWED');
        case PODReviewStatus.MACHINE_APPROVED:
            return t('enum.podReviewStatus.MACHINE_APPROVED');
        case PODReviewStatus.MACHINE_DISAPPROVED:
            return t('enum.podReviewStatus.MACHINE_DISAPPROVED');
        case PODReviewStatus.MANUAL_PENDING:
            return t('enum.podReviewStatus.MANUAL_PENDING');
        case PODReviewStatus.MANUAL_APPROVED:
            return t('enum.podReviewStatus.MANUAL_APPROVED');
        case PODReviewStatus.MANUAL_DISAPPROVED:
            return t('enum.podReviewStatus.MANUAL_DISAPPROVED');
        case PODReviewStatus.MANUAL_CORRECTED:
            //特殊处理 人审误判修正视作人审通过  展示也作为人审通过展示
            return t('enum.podReviewStatus.MANUAL_APPROVED');
        default:
            return t('enum.podReviewStatus.UNKNOWN');
    }
};

// 格式化POD Review评论显示
const formatPodReviewMessage = (messageList: string[] | PodReviewMessageItemType[] | undefined, t: any): string => {
    // 如果是 PodReviewMessageItemType[] 类型，先转换为 string[]
    let stringList: string[] | undefined;
    if (messageList && messageList.length > 0 && typeof messageList[0] === 'object') {
        stringList = getTranslatedPodReviewMessage(messageList as PodReviewMessageItemType[]) || undefined;
    } else {
        stringList = messageList as string[] | undefined;
    }

    if (!stringList || stringList.length === 0) return '';

    // 处理通过状态
    if (stringList.length === 1 && stringList[0] === 'MANUAL_APPROVED') {
        return t('enum.podReviewStatus.MANUAL_APPROVED');
    }

    // 处理不通过原因
    const predefinedReasons = ['NO_LABEL_PHOTO', 'WRONG_TRK', 'NO_HOUSE_APT', 'WRONG_HOUSE_APT'];
    const formattedParts = stringList.map(message => {
        if (predefinedReasons.includes(message)) {
            return t(`podReview.reasons.${message}`);
        } else {
            return message;
        }
    });

    return formattedParts.join('; ');
};

// 模块标题组件
interface SectionTitleProps {
    title: string;
}

const SectionTitle: React.FC<SectionTitleProps> = ({ title }) => (
    <div className="col-span-2 mt-6 mb-4 first:mt-0">
        <h4 className="text-base font-semibold text-gray-800 mb-2">{title}</h4>
        <div className="border-b border-gray-300"></div>
    </div>
);

// 上下布局的字段组件
interface VerticalFieldProps {
    label: string;
    value?: React.ReactNode;
    span?: 1 | 2;
}

const VerticalField: React.FC<VerticalFieldProps> = ({ label, value, span = 1 }) => (
    <div className={`mb-3 ${span === 2 ? 'col-span-2' : ''}`}>
        <div className="text-gray-500 text-sm mb-1">{label}</div>
        <div className="break-words">{value || '-'}</div>
    </div>
);

interface AccessCodeItem {
    id?: number;
    type: string;
    value: string;
    status?: number | null;
}

/** 门禁code值校验：只允许数字、字母、#和*；#和*不连续出现且只能在开头或结尾；最多8字符 */
function validateAccessCodeValue(value: string): string {
    let s = value.replace(/[^0-9a-zA-Z#*]/g, '').slice(0, 8);
    let leading = '';
    let trailing = '';
    if (s[0] === '#' || s[0] === '*') {
        leading = s[0];
        s = s.slice(1);
    }
    if (s.length > 0 && (s[s.length - 1] === '#' || s[s.length - 1] === '*')) {
        trailing = s[s.length - 1];
        s = s.slice(0, -1);
    }
    const middle = s.replace(/[#*]/g, '');
    if (leading && trailing && middle.length === 0) {
        trailing = '';
    }
    return leading + middle + trailing;
}

const CentralParcelDetailDrawer: React.FC<CentralParcelDetailDrawerProps> = ({
                                                               visible,
                                                               onClose,
                                                               orderId,
                                                               userRole,
                                                               onRefreshList,
                                                               hideEditActions = false,
                                                               extraActionButtons,
                                                               hidePrintAndPodReview = false,
                                                               showExternalTrack = false,
                                                               showInterceptionExemptionAlert = false,
                                                               isHistory = false,
                                                               embeddedReadOnly = false
                                                           }) => {
    const location = useLocation();
    const isCSRole = isCS();
    const isKAQCRole = isKAQC();
    const isCSFollowUpRole = isCSFollowUp();
    const isHeadquartersRole = hasHeadquartersAccess();
    const isFromQualityControl = location.pathname.includes('/qualitycontrol');
    const shouldHidePrintAndExceptionButtons = isKAQCRole || isCSFollowUpRole || (isHeadquartersRole && isFromQualityControl);

    // 检查是否有权限操作POD照片更新功能（在不隐藏编辑操作时）
    const hasPodUpdatePermission = !hideEditActions && (hasHeadquartersAccess() || hasResource([
        ResourceCode.menu.HUB_PACKAGE_DETAIL,
        ResourceCode.menu.WHS_PACKAGE_DETAIL,
        ResourceCode.menu.HUB_EXCEPTION_DETAIL,
        ResourceCode.menu.WHS_EXCEPTION_DETAIL,
        ResourceCode.menu.HUB_LABEL_SEARCH_DETAIL,
        ResourceCode.menu.WHS_LABEL_SEARCH_DETAIL,
    ]));

    const [podReview, setPodReview] = useState<number>(PODReviewStatus.NOT_REVIEWED);
    const [fakePod, setFakePod] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [markAsExceptionVisible, setMarkAsExceptionVisible] = useState(false);
    const [markAsExceptionLoading, setMarkAsExceptionLoading] = useState(false);
    const [failedReason,setFailedReason] = useState('');
    const [addToPodReviewLoading, setAddToPodReviewLoading] = useState(false);
    const [channelName,setChannelName] = useState('');
    const [recipientInfo, setRecipientInfo] = useState<AddressInfo>();
    const [originalRecipientInfo, setOriginalRecipientInfo] = useState<AddressInfo>();
    const [sender, setSender] = useState<AddressInfo>();
    const [parcelInfo, setParcelInfo] = useState<DimensionInfo>();
    const [editingBlock, setEditingBlock] = useState<null | 'recipient' | 'dimension'>(null);
    const [displayPodPhotos, setDisplayPodPhotos] = useState<string[]>([]);
    const [podPhotoObjectKeys, setPodPhotoObjectKeys] = useState<string[]>([]);
    // 使用 ref 保存原始图片路径（podImageS3KeysJson），用于判断图片是否被修改
    const originalPodPhotoObjectKeysRef = useRef<string[]>([]);
    const [podPhotosChanged, setPodPhotosChanged] = useState(false);
    // 使用 ref 保存页面加载时的原始尺寸值，用于判断尺寸是否被修改
    const originalDimensionsRef = useRef<{ lengthCm: number | null; widthCm: number | null; heightCm: number | null }>({
        lengthCm: null,
        widthCm: null,
        heightCm: null
    });
    const [submittingPodPhotos, setSubmittingPodPhotos] = useState(false);
    const [accessInfoExpanded, setAccessInfoExpanded] = useState(false);
    const [accessCodeList, setAccessCodeList] = useState<AccessCodeItem[]>([]);
    const [accessCodeListEditing, setAccessCodeListEditing] = useState<AccessCodeItem[]>([]);
    const [isAccessCodeEditing, setIsAccessCodeEditing] = useState(false);
    const [visibleAccessCodeIndices, setVisibleAccessCodeIndices] = useState<Set<number>>(new Set());
    const [changeAddress] = useMutation(CHANGE_ORDER_RECEIVER_ADDRESS);
    const [changeRecipientContact] = useMutation(CHANGE_ORDER_RECIPIENT_CONTACT);
    const [generatePdf] = useMutation<GenerateShippingLabelPdfResponse, GenerateShippingLabelPdfVariables>(GENERATE_SHIPPING_LABEL_PDF);
    const [batchMarkForManualReview] = useMutation<BatchMarkForManualReviewResponse>(BATCH_MARK_FOR_MANUAL_REVIEW);
    const [updateActualDimension] = useMutation(UPDATE_ORDER_ACTUAL_DIMENSION);
    const { t, i18n } = useTranslation();
    const { getExceptionReasonDisplayText } = useStatusInternationalization();

    // 🌍 状态国际化处理函数
    const getStatusText = (status: string): string => {
        return t(`enum.overallStatus.${status}`, { defaultValue: status });
    };

    // 🌍 投诉状态国际化处理函数
    const getComplaintText = (isComplaint: boolean): string => {
        return isComplaint ? t('enum.yesorNoStatus.yes') : t('enum.yesorNoStatus.no');
    };

    // 🌍 下一步动作国际化处理函数
    const getNextActionText = (nextAction: string): string => {
        if (!nextAction) return '-';
        // 将首字母转为小写以匹配翻译配置
        const actionKey = nextAction.charAt(0).toLowerCase() + nextAction.slice(1);
        return t(`config.nextAction.${actionKey}`, { defaultValue: nextAction });
    };

    // 查询异常单状态枚举
    const { data: exceptionStatusEnumData } = useQuery<GetIntEnumValuesResponse>(GET_INT_ENUM_VALUES, {
        variables: { enumType: 'ExceptionOrderStatus' }
    });
    const exceptionStatusMap = React.useMemo(() => {
        const map: Record<number, string> = {};
        (exceptionStatusEnumData?.getIntEnumValues || []).forEach(item => {
            map[item.value] = item.name;
        });
        return map;
    }, [exceptionStatusEnumData]);

    // 异常状态国际化处理函数（使用异常单三态枚举，与WMS异常页面一致）
    const getExceptionStatusText = (isHasBeenSetException: boolean, isAbnormal: boolean, exceptionStatus?: number | null): string => {
        if (!isHasBeenSetException) return '-';
        if (exceptionStatus != null) {
            const name = exceptionStatusMap[exceptionStatus];
            if (name) {
                return t(`central.exceptions.exceptionStatus.${name}`);
            }
        }
        return isAbnormal ? t('config.exceptionStatus.open') : t('config.exceptionStatus.resolved');
    };

    //detail
    // 历史模式打 getOrderDetailForHistory（数据来自 order_history / order_event_history），
    // 其余 8 个辅助查询不变——它们查的是枚举/字典/渠道等参考数据，不是订单数据
    const detailQuery = isHistory ? GET_ORDER_DETAIL_FOR_HISTORY : GET_ORDER_DETAIL_FOR_CENTRAL;
    const { data, loading: queryLoading, refetch } = useQuery<
        CentralOrderDetailResponse & Partial<HistoryOrderDetailResponse>
    >(detailQuery, {
            variables: { orderId: orderId }, skip: !visible || !orderId,
            onError: (error) => {
                if (getErrorKeys(error).messageKey === 'errors.business.order.notExist') {
                    showError(error);
                }
            }});
    const parcel = isHistory ? data?.getOrderDetailForHistory : data?.getOrderDetailForCentral;
    const { submitLocation } = useSubmitOrderAddressLocationChange({
        orderId,
        refetch,
    });
    const temuSortingSegments = resolveTemuDisplayFromOrder(parcel?.orderWithAddresses?.order);
    //failedReason
    const { data: exceptionData } = useQuery<ExceptionDictionaryByCodeResponse>(GET_EXCEPTION_DICTIONARY_BY_REASON_CODE,{
        variables:{exceptionReasonCode:parcel?.orderWithAddresses?.order?.exceptionReasonCode},
        skip: !visible || !parcel?.orderWithAddresses?.order?.exceptionReasonCode
    });
    const exception  = exceptionData?.getExceptionDictionaryByReasonCode;
    //channel
    const{data:channelData} = useQuery<channelTypeResponse>(GET_CHANNEL,{
        variables:{id:parcel?.orderWithAddresses?.order?.channelId},
        skip: !visible || !parcel?.orderWithAddresses?.order?.channelId
    })
    const channel = channelData?.getChannel;
    const orderIdNum = orderId ? parseInt(orderId) : NaN;
    const { data: accessCodeData, loading: accessCodeLoading, refetch: refetchAccessCodes } = useQuery<GetAccessCodesByOrderIdResponse, GetAccessCodesByOrderIdVariables>(
        GET_ACCESS_CODES_BY_ORDER_ID,
        {
            variables: { orderId: orderIdNum },
            skip: !visible || !orderId || isNaN(orderIdNum),
            fetchPolicy: 'network-only'
        }
    );
    const [deleteAccessCodeMutation] = useMutation<DeleteAccessCodeResponse, DeleteAccessCodeVariables>(DELETE_ACCESS_CODE);
    const [createAccessCodeMutation] = useMutation<CreateAccessCodeResponse, CreateAccessCodeVariables>(CREATE_ACCESS_CODE);
    const [updateAccessCodeMutation] = useMutation<UpdateAccessCodeResponse, UpdateAccessCodeVariables>(UPDATE_ACCESS_CODE);
    const [savePcAccessCodeQueryLog] = useMutation<SavePcAccessCodeQueryLogResponse, SavePcAccessCodeQueryLogVariables>(SAVE_PC_ACCESS_CODE_QUERY_LOG);
    //Exception
    const [markAsException] = useMutation(MARK_AS_EXCEPTION);
    // 妥投撤回
    const [revokeDelivery, { loading: revokeLoading }] = useMutation<RevokeDeliveryResponse, RevokeDeliveryVariables>(REVOKE_DELIVERY);
    const [showRevokeModal, setShowRevokeModal] = useState(false);
    const [addressGuideOpen, setAddressGuideOpen] = useState(false);
    const [selectedRevokeReason, setSelectedRevokeReason] = useState<string>('');
    const { data: revokeReasonData } = useQuery<GetDeliveryRevokeReasonI18nConfigResponse>(
        GET_DELIVERY_REVOKE_REASON_I18N_CONFIG,
        { fetchPolicy: 'network-only', skip: !visible }
    );
    const revokeReasons = revokeReasonData?.getDeliveryRevokeReasonI18nConfig?.reasons ?? [];

    const [podImageUpdate] = useMutation<PodImageUpdateResponse, {input: PodImageUpdateInput}>(POD_IMAGE_UPDATE);
    const [zoneValue, setZoneValue] = useState<string | null>(null);
    const [loadZone] = useLazyQuery(GET_ZONE, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => setZoneValue(data?.getZone ?? null),
        onError: () => setZoneValue(null),
    });

    // 获取仓库信息进行数据隔离
    const { currentWarehouse } = useWarehouseContext();
    const siteType = currentWarehouse?.siteType;
    const warehouseId = currentWarehouse?.id ? currentWarehouse?.id : null;

    // 使用公共的更新备注 hook
    const { handleUpdateNote, loading: noteLoading } = useUpdateOrderNote({
        orderId: orderId,
        trackingNumber: parcel?.orderWithAddresses?.order?.trackingNumber,
        warehouseId: warehouseId,
        refetch: refetch
    });

    useEffect(() => {
        if (parcel){
            const originZipcode = parcel?.orderWithAddresses?.pickupAddress?.zipcode;
            const targetZipcode = parcel?.orderWithAddresses?.deliveryAddress?.zipcode;

            if (originZipcode && targetZipcode) {
                loadZone({
                    variables:{
                        originZipcode,
                        targetZipcode
                    }
                });
            } else {
                setZoneValue(null);
            }
        } else {
            setZoneValue(null);
        }
    }, [parcel, loadZone]);

    useEffect(() => {
        if (exception) setFailedReason(getExceptionReasonDisplayText(exception, parcel?.orderWithAddresses?.order?.exceptionSubReasons));
    }, [data, orderId, exceptionData]);

    useEffect(() => {
        setChannelName('');
        if (channel) setChannelName(channel.channelName);
    }, [data, orderId, channelData]);

    useEffect(() => {
        if (visible) {
            setIsAccessCodeEditing(false);
            setVisibleAccessCodeIndices(new Set());
            setAccessInfoExpanded(false);
            // 退出编辑时使用后端最新数据渲染，清空本地编辑缓存
            setAccessCodeListEditing([]);
        }
    }, [visible]);

    useEffect(() => {
        if (!visible || !orderId || accessCodeLoading) return;
        const list = accessCodeData?.getAccessCodesByOrderId;
        const mapped: AccessCodeItem[] = list ? list.map(item => ({
            id: item.id,
            type: item.codeType,
            value: item.accessCode ?? '',
            status: item.status ?? 0
        })) : [];
        setAccessCodeList(mapped);
        // 仅在非编辑态时同步编辑列表，避免覆盖正在编辑的内容
        if (!isAccessCodeEditing) {
            setAccessCodeListEditing([]);
        }
    }, [accessCodeData, accessCodeLoading, visible, orderId, isAccessCodeEditing]);

    useEffect(() => {
        setEditingBlock(null);
        setOriginalRecipientInfo(undefined);
        
        if (parcel) {
            setPodReview(parcel?.orderWithAddresses?.order?.podReviewStatus || PODReviewStatus.NOT_REVIEWED);
            
            // 加载明细数据时，需要先清空之前的pod图片记录
            originalPodPhotoObjectKeysRef.current = [];
            setPodPhotosChanged(false);
            
            // 然后记录当前订单的pod图片
            const displayPodPhotoUrls = parcel?.orderWithAddresses?.order?.podImageS3UrlsJson || []; // 全路径地址
            const currentPodPhotoObjectKeys = parcel?.orderWithAddresses?.order?.podImageS3KeysJson || [];
            setDisplayPodPhotos(displayPodPhotoUrls);
            setPodPhotoObjectKeys(currentPodPhotoObjectKeys);
            // 原始图片路径记录podImageS3KeysJson，用于和修改后的路径对比
            originalPodPhotoObjectKeysRef.current = currentPodPhotoObjectKeys;
            setRecipientInfo({
                fullName: parcel.orderWithAddresses?.deliveryAddress?.fullName || '',
                phoneCountry: `${parcel.orderWithAddresses?.deliveryAddress?.countryCode ? `+${parcel.orderWithAddresses?.deliveryAddress?.countryCode}` : '+1'}`,
                phoneNumber: parcel.orderWithAddresses?.deliveryAddress?.phoneNumber || '',
                phoneExt: parcel.orderWithAddresses?.deliveryAddress?.extension || '',
                addressLine1: parcel.orderWithAddresses?.deliveryAddress?.addressLine1 || '',
                addressLine2: parcel.orderWithAddresses?.deliveryAddress?.addressLine2 || '',
                country: 'US',
                state: parcel.orderWithAddresses?.deliveryAddress?.state || '',
                city: parcel.orderWithAddresses?.deliveryAddress?.city || '',
                zipcode: parcel.orderWithAddresses?.deliveryAddress?.zipcode || '',
                isFinished: parcel.orderWithAddresses?.order?.isFinished || parcel.orderWithAddresses?.order?.overallStatus === 'PRE_DELIVERED',
                geo: splicingGeoInfo(
                    parcel.orderWithAddresses?.deliveryAddress?.latitude,
                    parcel.orderWithAddresses?.deliveryAddress?.longitude
                ),
            });
            setSender({
                fullName: parcel.orderWithAddresses?.pickupAddress?.fullName || '',
                phoneCountry: `${parcel.orderWithAddresses?.pickupAddress?.countryCode ? `+${parcel.orderWithAddresses?.pickupAddress?.countryCode}` : '+1'}`,
                phoneNumber: parcel.orderWithAddresses?.pickupAddress?.phoneNumber || '',
                phoneExt: parcel.orderWithAddresses?.pickupAddress?.extension || '',
                addressLine1: parcel.orderWithAddresses?.pickupAddress?.addressLine1 || '',
                addressLine2: parcel.orderWithAddresses?.pickupAddress?.addressLine2 || '',
                country: 'US',
                state: parcel.orderWithAddresses?.pickupAddress?.state || '',
                city: parcel.orderWithAddresses?.pickupAddress?.city || '',
                zipcode: parcel.orderWithAddresses?.pickupAddress?.zipcode || '',
                geo: splicingGeoInfo(
                    parcel.orderWithAddresses?.pickupAddress?.latitude,
                    parcel.orderWithAddresses?.pickupAddress?.longitude
                ),
            });
            const originalLengthCm = parcel?.orderWithAddresses?.order?.lengthCm ?? null;
            const originalWidthCm = parcel?.orderWithAddresses?.order?.widthCm ?? null;
            const originalHeightCm = parcel?.orderWithAddresses?.order?.heightCm ?? null;
            
            // 保存页面加载时的原始尺寸值
            originalDimensionsRef.current = {
                lengthCm: originalLengthCm,
                widthCm: originalWidthCm,
                heightCm: originalHeightCm
            };
            
            setParcelInfo({
                lengthCm: originalLengthCm,
                widthCm: originalWidthCm,
                heightCm: originalHeightCm,
                packageValueUsd: parcel?.orderWithAddresses?.order?.packageValueUsd || null,
                volumetricWeight: parcel?.orderWithAddresses?.order?.volumetricWeightKg || null,
                weight: parcel?.orderWithAddresses?.order?.weightKg || null,
            });
        }
        if (podReview) setFakePod(podReview === PODReviewStatus.MANUAL_APPROVED ? 'N' : podReview === PODReviewStatus.MANUAL_DISAPPROVED ? 'Y' : null);
    }, [data, orderId]);

    const handleEditRecipient = () =>{
      setEditingBlock('recipient');
    };

    // 处理POD照片变化
    const handlePodPhotosChange = (newObjectKeys: string[]) => {
        setPodPhotoObjectKeys(newObjectKeys);
        // 当用户修改pod图片时，判断图片是否被修改了，和原始图片路径（podImageS3KeysJson）对比
        const originalKeys = originalPodPhotoObjectKeysRef.current;
        // 创建副本进行排序，避免修改只读数组
        const hasChanged = JSON.stringify([...newObjectKeys].sort()) !== JSON.stringify([...originalKeys].sort());
        setPodPhotosChanged(hasChanged);
    };

    // 获取POD图片更新错误消息的国际化处理
    const getPodImageUpdateErrorMessage = (errorCode?: string): string => {
        if (!errorCode) {
            return t('central.parcels.messages.failedToUpdatePodPhotos');
        }

        switch (errorCode) {
            case 'EMPTY_POD_IMAGE_LIST':
                return t('wms.parcels.messages.emptyPodImageList');
            case 'PARCEL_NOT_FOUND':
                return t('wms.parcels.messages.parcelNotFound');
            case 'NON_DELIVERED_NOT_UPDATE_POD_IMAGE':
                return t('central.parcels.messages.nonDeliveredNotUpdatePodImage');
            case 'POD_IMAGE_MAST_3_to_6':
                return t('wms.parcels.messages.podPhotosMustBe3To6');
            default:
                return t('central.parcels.messages.failedToUpdatePodPhotos');
        }
    };

    // 提交POD照片修改
    const handleSubmitPodPhotos = async () => {
        if (!podPhotosChanged) return;

        // 验证图片数量
        if (podPhotoObjectKeys.length < 3 || podPhotoObjectKeys.length > 6) {
            message.error(t('wms.parcels.messages.podPhotosMustBe3To6'));
            return;
        }

        setSubmittingPodPhotos(true);

        try {
            // 调用API来更新POD照片
            const result = await podImageUpdate({
                variables: {
                    input: {
                        warehouseId: warehouseId,
                        trackingNumber: parcel?.orderWithAddresses?.order?.trackingNumber || '',
                        podUpdateImageList: podPhotoObjectKeys
                    }
                }
            });

            const podImageUpdateResult = result.data?.podImageUpdate;

            if (podImageUpdateResult?.success) {
                message.success(t('central.parcels.messages.podPhotosUpdatedSuccessfully'));
                setPodPhotosChanged(false);
                // 更新原始图片路径记录
                originalPodPhotoObjectKeysRef.current = podPhotoObjectKeys;
                // 刷新数据
                refetch();
            } else {
                // 处理错误码的国际化
                const errorMessage = getPodImageUpdateErrorMessage(podImageUpdateResult?.errorCode);
                message.error(errorMessage);
            }
        } catch (error) {
            console.error('Failed to update POD photos:', error);
            message.error(t('central.parcels.messages.failedToUpdatePodPhotos'));
        } finally {
            setSubmittingPodPhotos(false);
        }
    };

    const handleUpdateAddress = async (formData: AddressInfo) => {
        if (parcel?.orderWithAddresses?.order?.overallStatus === 'DELIVERED' || parcel?.orderWithAddresses?.order?.overallStatus === 'PRE_DELIVERED') {
            message.warning(t('central.parcels.messages.packageHasBeenDelivered'));
            return;
        }

        setOriginalRecipientInfo(recipientInfo);
        const originalInfo = recipientInfo;
        
        try {
            if (orderId) {
                await changeAddress({
                    variables: {
                        orderId: orderId,
                        address: {
                            fullName: formData.fullName,
                            phoneNumber: formData.phoneNumber,
                            email: '',
                            addressLine1: formData.addressLine1,
                            addressLine2: formData.addressLine2,
                            countryCode: parseInt(formData.phoneCountry || '1'),
                            extension: formData.phoneExt || '',
                            city: formData.city,
                            state: formData.state,
                            zipcode: formData.zipcode,
                        }
                    }
                });
                setRecipientInfo(formData);
                setEditingBlock(null);
                message.success(t('central.parcels.messages.addressModificationSuccessful'));
                // 重新加载订单详情数据
                refetch();
            }
        } catch (error) {
            const { titleKey, messageKey } = getErrorKeys(error);
            if (messageKey === 'errors.business.order.recipientAddressChangedSortingCodeNotMatch') {
                Modal.warning({
                    title: t('central.parcels.pageDetails.changeAddress'),
                    content: t('central.parcels.messages.cannotChangeToAnotherSortingCode'),
                    keyboard: false,
                    maskClosable: false,
                });
            } else if (messageKey === 'errors.business.order.recipientAddressChangeWillChangeZone') {
                Modal.error({
                    title: t('central.parcels.messages.addressModificationFailed'),
                    content: t('errors.business.order.recipientAddressChangeWillChangeZone'),
                    keyboard: false,
                    maskClosable: false,
                });
            } else {
                console.error('Address modification failed:', error);
                // 错误编码取后端返回的原始编码（extensions.message，如 ADDRESS_BLACKLIST），
                // 错误描述按 extensions.messageKey 取国际化文案，无译文时兜底显示原始消息
                let errorCode = '-';
                let rawMessage = '';
                if (error instanceof Error && 'graphQLErrors' in error) {
                    const apolloError = error as any;
                    if (apolloError.graphQLErrors && apolloError.graphQLErrors.length > 0) {
                        const graphQLError = apolloError.graphQLErrors[0];
                        rawMessage = graphQLError.extensions?.message || graphQLError.message || '';
                        errorCode = rawMessage || errorCode;
                    }
                }
                // 该 messageKey 的通用文案被其他页面共用，本页统一格式弹窗使用独立文案 key
                const descriptionKey = messageKey === 'errors.business.order.recipientAddressChangeWillChangeDeliveryWhs'
                    ? 'errors.business.order.addressModifyChangeWillChangeDeliveryWhsError'
                    : messageKey;
                const translated = descriptionKey ? t(descriptionKey) : '';
                const errorDescription = (translated && translated !== descriptionKey)
                    ? translated
                    : (rawMessage || t('central.parcels.messages.addressModificationFailed'));
                Modal.error({
                    title: t('central.parcels.messages.addressModificationFailed'),
                    content: (
                        <div>
                            <div>{t('central.parcels.messages.errorCode')}: {errorCode}</div>
                            <div>
                                {t('central.parcels.messages.errorDescription')}: {errorDescription}
                                {messageKey === 'errors.business.order.addressModifyConflictError' && (
                                    <Button
                                        type="link"
                                        className="px-0"
                                        style={{ height: 'auto', lineHeight: 1.2, color: '#722ED1' }}
                                        onClick={() => setAddressGuideOpen(true)}
                                    >
                                        {t('delivery.parcels.guide.viewAddressGuide')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    ),
                    keyboard: false,
                    maskClosable: false,
                    okButtonProps: { style: { backgroundColor: '#722ED1', borderColor: '#722ED1' } },
                });
            }

            // 发生错误时恢复原始值
            setRecipientInfo(originalInfo);
            setOriginalRecipientInfo(undefined);
        }
    };

    const handleUpdateContact = async (formData: AddressInfo) => {
        if (parcel?.orderWithAddresses?.order?.overallStatus === 'DELIVERED' || parcel?.orderWithAddresses?.order?.overallStatus === 'PRE_DELIVERED') {
            message.warning(t('central.parcels.messages.packageHasBeenDelivered'));
            return;
        }

        const originalInfo = recipientInfo;
        try {
            if (orderId) {
                const countryCodeRaw = (formData.phoneCountry || '1').replace(/^\+/, '');
                const countryCode = parseInt(countryCodeRaw, 10) || 1;
                await changeRecipientContact({
                    variables: {
                        orderId: orderId,
                        fullName: formData.fullName,
                        phoneNumber: formData.phoneNumber,
                        countryCode,
                        extension: formData.phoneExt || null,
                    }
                });
                setRecipientInfo(formData);
                setEditingBlock(null);
                message.success(t('central.parcels.messages.contactModificationSuccessful'));
                refetch();
            }
        } catch (error) {
            console.error('Recipient contact modification failed:', error);
            message.error(t('central.parcels.messages.contactModificationFailed'));
            setRecipientInfo(originalInfo);
        }
    };

    const handleUpdateActualDimension = async (formData: ActualDimensionInfo) => {
        if (parcel?.orderWithAddresses?.order?.isFinished) {
            message.warning(t('central.parcels.messages.packageFinishedWarning') || '包裹已完结，无法修改实际尺寸');
            return;
        }

        // 获取页面加载时的原始尺寸值（从 ref 中获取，确保是页面加载时的初始值）
        const originalLengthCm = originalDimensionsRef.current.lengthCm;
        const originalWidthCm = originalDimensionsRef.current.widthCm;
        const originalHeightCm = originalDimensionsRef.current.heightCm;

        // 比较新值与原始值是否相同（处理 null/undefined 和数值比较的情况）
        const normalizeValue = (val: number | null | undefined): number | null => {
            if (val == null) return null;
            return val;
        };
        
        const newLength = normalizeValue(formData.lengthCm);
        const newWidth = normalizeValue(formData.widthCm);
        const newHeight = normalizeValue(formData.heightCm);
        
        // 比较函数：处理 null 和数值精度问题
        const isValueUnchanged = (newVal: number | null, originalVal: number | null): boolean => {
            // 如果都是 null，认为未变动
            if (newVal === null && originalVal === null) return true;
            // 如果一个是 null，另一个不是，认为已变动
            if (newVal === null || originalVal === null) return false;
            // 数值比较：允许小的精度误差（0.001 cm）
            return Math.abs(newVal - originalVal) < 0.001;
        };
        
        const lengthUnchanged = isValueUnchanged(newLength, originalLengthCm);
        const widthUnchanged = isValueUnchanged(newWidth, originalWidthCm);
        const heightUnchanged = isValueUnchanged(newHeight, originalHeightCm);
        const dimensionsUnchanged = lengthUnchanged && widthUnchanged && heightUnchanged;

        // 如果长宽高没有变动，不允许提交（无论原始值是否为 null）
        if (dimensionsUnchanged) {
            message.warning(t('delivery.parcels.message.dimensionUnchangedWarning') || '实际尺寸未变动，无法提交修改');
            return;
        }

        try {
            if (orderId) {
                const input: UpdateOrderActualDimensionInput = {
                    orderId: parseInt(orderId),
                    lengthCm: formData.lengthCm ?? undefined,
                    widthCm: formData.widthCm ?? undefined,
                    heightCm: formData.heightCm ?? undefined,
                };

                await updateActualDimension({
                    variables: {
                        input: input
                    }
                });

                // 更新本地状态
                setParcelInfo({
                    lengthCm: formData.lengthCm,
                    widthCm: formData.widthCm,
                    heightCm: formData.heightCm,
                    packageValueUsd: parcelInfo?.packageValueUsd ?? null,
                    volumetricWeight: parcelInfo?.volumetricWeight ?? null,
                    weight: parcelInfo?.weight ?? null,
                });

                setEditingBlock(null);
                message.success(t('delivery.parcels.message.dimensionModifySuccess'));
                // 重新加载订单详情数据
                refetch();
            }
        } catch (error) {
            let errorMessage = t('delivery.parcels.message.dimensionModifyFailed') || '实际尺寸更新失败';
            if (error instanceof Error && 'graphQLErrors' in error) {
                const apolloError = error as any;
                if (apolloError.graphQLErrors && apolloError.graphQLErrors.length > 0) {
                    const graphQLError = apolloError.graphQLErrors[0];
                    errorMessage = graphQLError.extensions?.message || graphQLError.message || errorMessage;
                }
            }
            message.error(errorMessage);
        }
    };

    // 处理评价提交
    const handleReviewSubmit = async (review: PODReviewStatus.MANUAL_DISAPPROVED | PODReviewStatus.MANUAL_APPROVED) => {
        if (!parcel) return;

        setLoading(true);

        try {
            const success = await submitPodReview(orderId, review);

            if (success) {
                if (podReview === review) {
                    setPodReview(PODReviewStatus.NOT_REVIEWED);
                    message.success(t('central.parcels.messages.evaluationHasBeenCancelled'));
                } else {
                    setPodReview(review);
                    message.success(review === PODReviewStatus.MANUAL_APPROVED ? t('central.parcels.messages.podEvaluationSuccessful') : t('central.parcels.messages.podEvaluationSuccessfulDissatisfied'));
                }
            } else {

                message.error( t('central.parcels.messages.failedToSubmitEvaluationPleaseTryAgain'));
            }
        } catch (error) {
            message.error(t('central.parcels.messages.failedToSubmitEvaluationPleaseTryAgain'));
        } finally {
            setLoading(false);
        }
    };

    // 获取状态对应的颜色
    const getStatusBgColor = (status: string) => {
        switch (status) {
            case 'CREATED':
                return 'bg-blue-50 text-blue-600 border border-blue-200';
            case 'CANCELLED':
                return 'bg-gray-50 text-gray-600 border border-gray-200';
            case 'ARRIVED_ORIGIN_HUB':
                return 'bg-purple-50 text-purple-600 border border-purple-200';
            case 'PENDING_SHIPMENT':
            case 'LINEHAUL_PENDING_SHIPMENT':
            case 'FEEDER_PENDING_SHIPMENT':
                return 'bg-yellow-50 text-yellow-600 border border-yellow-200';
            case 'HANDED_OVER':
                return 'bg-indigo-50 text-indigo-600 border border-indigo-200';
            case 'IN_TRANSIT':
                return 'bg-cyan-50 text-cyan-600 border border-cyan-200';
            case 'ARRIVED_DESTINATION_HUB':
                return 'bg-teal-50 text-teal-600 border border-teal-200';
            case 'ARRIVED_WHS':
                return 'bg-emerald-50 text-emerald-600 border border-emerald-200';
            case 'WAITING_FOR_DELIVERY':
                return 'bg-orange-50 text-orange-600 border border-orange-200';
            case 'DELIVERY_IN_PROGRESS':
                return 'bg-amber-50 text-amber-600 border border-amber-200';
            case 'DELIVERED':
                return 'bg-green-50 text-green-600 border border-green-200 font-semibold';
            case 'DELIVERY_FAILED':
                return 'bg-red-50 text-red-600 border border-red-200';
            case 'PENDING_INTERCEPTION':
                return 'bg-pink-50 text-pink-600 border border-pink-200';
            case 'INTERCEPTED':
                return 'bg-rose-50 text-rose-600 border border-rose-200';
            case 'RETURN_STARTED':
                return 'bg-violet-50 text-violet-600 border border-violet-200';
            case 'RETURNED':
                return 'bg-slate-50 text-slate-600 border border-slate-200';
            case 'HOLDING':
                return 'bg-stone-50 text-stone-600 border border-stone-200';
            case 'RETURN_ARRIVED_DESTINATION_HUB':
                return 'bg-fuchsia-50 text-fuchsia-600 border border-fuchsia-200';
            case 'PICKUP_FAILED':
                return 'bg-red-50 text-red-600 border border-red-200';
            default:
                return 'bg-gray-50 text-gray-600 border border-gray-200';
        }
    };

    const handleMarkAsException = () => {
        setMarkAsExceptionVisible(true);
    };

    const handleMarkAsExceptionConfirm = async (formData:MarkAsExceptionData) => {
        try {
            setMarkAsExceptionLoading(true);

            await markAsException({
                variables: {
                    orderId: orderId,
                    exceptionReasonCode: formData.exceptionReasonCode,
                    exceptionHubId: formData.exceptionHubId? formData.exceptionHubId : (siteType == SiteType.HUB ? currentWarehouse?.id : currentWarehouse?.parentSiteId),
                    exceptionWarehouseId: formData.exceptionWarehouseId? formData.exceptionWarehouseId : (siteType == SiteType.WAREHOUSE ? currentWarehouse?.id : null),
                    exceptionImageS3KeysJson: formData.photos,
                    exceptionSubReasons: formData.exceptionSubReasons ?? undefined
                }
            })

            message.success(t('central.parcels.messages.markingExceptionSuccessfully'));
            // 刷新详情页
            refetch();
            // 刷新外部列表
            onRefreshList?.();
        } catch (error) {
            console.error('Marking exception failed:', error);
            message.error(t('central.parcels.messages.markingExceptionFailed'));
        } finally {
            setMarkAsExceptionLoading(false);
        }
    };

    const handleAddToPodReview = async () => {
        // 防重复点击 - 放在最前面
        if (addToPodReviewLoading) {
            return;
        }

        // 立即设置loading状态，防止重复点击
        setAddToPodReviewLoading(true);

        if (!orderId) {
            message.error(t('podReview.messages.orderIdCannotBeEmpty'));
            // 短暂延迟后重置loading状态
            setTimeout(() => setAddToPodReviewLoading(false), 1000);
            return;
        }

        // 检查运单状态，只有妥投状态才能添加到人审
        if (parcel?.orderWithAddresses?.order?.overallStatus !== 'DELIVERED') {
            message.warning(t('podReview.messages.onlyDeliveredOrdersCanAddToPodReview'));
            // 短暂延迟后重置loading状态
            setTimeout(() => setAddToPodReviewLoading(false), 1000);
            return;
        }
        try {
            const {data} = await batchMarkForManualReview({
                variables: {
                    orderIds: [parseInt(orderId)]
                },
                refetchQueries: ['CentralQuery']
            });

            if (data?.batchMarkForManualReview) {
                const result = data.batchMarkForManualReview;
                if (result.success) {
                    if (result.markedCount > 0) {
                        message.success(t('podReview.messages.addToPodReviewSuccess', { count: result.markedCount }));
                        refetch();
                    } else {
                        // 详情页接口失败时固定返回latestPodsAlreadyReviewed
                        message.error(t('podReview.messages.latestPodsAlreadyReviewed'));
                    }
                } else {
                    // 详情页接口失败时固定返回latestPodsAlreadyReviewed
                    message.error(t('podReview.messages.latestPodsAlreadyReviewed'));
                }
            } else {
                message.error(t('podReview.messages.latestPodsAlreadyReviewed'));
            }
        } catch (error: any) {
            console.error('Add to POD review error:', error);
            message.error(t('podReview.addToPodReview.failedToAdd'));
        } finally {
            setAddToPodReviewLoading(false);
        }
    };

    const handlePrintLabel = async () => {
        if (!orderId){
            message.error(t('central.parcels.messages.orderIDCannotBeEmpty'));
            return;
        }
        try {
            const { data } = await generatePdf({variables: { orderId: Number(orderId) }});

            if (data?.generateShippingLabelPdf) {
                const filename = `shipping_labels_${parcel?.orderWithAddresses?.order?.trackingNumber || parcel?.orderWithAddresses?.order?.id}.pdf`;
                printPdfFromBase64(data.generateShippingLabelPdf, filename);
            }
        } catch (error) {
            console.error('Parcel Details Print Label failed:', error);
            message.error(translateGraphQLError(error, 'central.parcels.messages.failedToGenerateShippingLabels'));
        }
    };
    
    if (!data){
        return (
            <Drawer
                title={
                    <div className="flex justify-between items-center">
                        <div className="flex items-center">
                            <span className="h-5 border-l-4 border-purple-500 mr-2 mt-1"></span>
                            <span className="text-xl font-medium text-black">{t('central.parcels.pageDetails.parcelDetail')}</span>
                        </div>
                        <CloseOutlined onClick={onClose} className="cursor-pointer"/>
                    </div>
                }
                width={1080}
                placement="right"
                closable={false}
                onClose={onClose}
                open={visible}
            >
                <Skeleton active />
            </Drawer>
        )
    }

    return (
        <Drawer
            title={
                <div className="flex justify-between items-center">
                    <div className="flex items-center">
                        <span className="h-5 border-l-4 border-purple-500 mr-2 mt-1"></span>
                        <span className="text-xl font-medium text-black">{t('central.parcels.pageDetails.parcelDetail')}</span>
                    </div>
                    <CloseOutlined onClick={onClose} className="cursor-pointer"/>
                </div>
            }
            width={window.innerWidth * 0.7}
            placement="right"
            closable={false}
            onClose={onClose}
            open={visible}
            styles={{
                body: {
                    padding: '24px',
                    height: 'calc(100vh - 56px)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            {parcel && (
                <div className="flex flex-1 overflow-hidden w-full">
                    <div className="w-3/5 overflow-y-auto pr-4">
                        {/* 两列网格布局 */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1">
                            {/* ========== 基础信息模块 ========== */}
                            <SectionTitle title={t('central.parcels.sections.basicInfo')} />

                            {/* 运单号 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.tracking') + ':'}
                                value={parcel?.orderWithAddresses?.order?.trackingNumber}
                            />

                            {/* 大包号 */}
                            {parcel?.clientCartonNo && (
                                <VerticalField
                                    label={t('central.parcels.pageDetails.clientCartonNo') + ':'}
                                    value={parcel.clientCartonNo}
                                />
                            )}

                            {/* 订单号（CS角色不显示） */}
                            {!isCSRole && (
                                <VerticalField
                                    label={t('central.parcels.pageDetails.order') + ':'}
                                    value={parcel?.orderWithAddresses?.order?.channelOrderNumber}
                                />
                            )}

                            {/* 状态 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.status') + ':'}
                                value={
                                    <span className={`inline-block px-3 py-1.5 rounded-md text-sm font-medium ${getStatusBgColor(parcel?.orderWithAddresses?.order?.overallStatus || '')}`}>
                                        {getStatusText(parcel?.orderWithAddresses?.order?.overallStatus || '')}
                                    </span>
                                }
                            />

                            {/* 配送时间（CS角色不显示） */}
                            {!isCSRole && (
                                <VerticalField
                                    label={t('central.parcels.pageDetails.deliveredTime') + ':'}
                                    value={parcel?.orderWithAddresses?.order?.deliveredAt ? formatTimestampToDateTime(parcel?.orderWithAddresses?.order?.deliveredAt) : '-'}
                                />
                            )}

                            {/* 结算方式 */}
                            <VerticalField
                                label={t('central.channels.settlement.settlementMode') + ':'}
                                value={(() => {
                                    const mode = parcel?.settlementMode;
                                    if (mode === 1) {
                                        return t('central.channels.settlement.settlementModeOptions.payRealTime');
                                    }
                                    if (mode === 0) {
                                        return t('central.channels.settlement.settlementModeOptions.payLaterBill');
                                    }
                                    return '-';
                                })()}
                            />

                            {/* 分拣单号 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.sortingChannelOrderNumber') + ':'}
                                value={parcel?.sortingChannelOrderNumber || '-'}
                            />

                            {/* 订单类型 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.orderType') + ':'}
                                value={parcel?.orderType
                                    ? t(`central.parcels.pageDetails.orderTypeOptions.${parcel.orderType}`, { defaultValue: parcel.orderType })
                                    : '-'}
                            />

                            {/* ========== 寄件信息模块 ========== */}
                            <SectionTitle title={t('central.parcels.sections.senderInfo')} />

                            {/* 客户（非CS角色显示） */}
                            {!isCSRole && (
                                <VerticalField
                                    label={t('central.parcels.pageDetails.client') + ':'}
                                    value={channelName}
                                />
                            )}

                            {/* 订单号（非CS角色显示） */}
                            {!isCSRole && (
                                <VerticalField
                                    label={t('central.parcels.pageDetails.order') + ':'}
                                    value={parcel?.orderWithAddresses?.order?.channelOrderNumber || '-'}
                                />
                            )}

                            {/* 3. 订单时间 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.orderTime') + ':'}
                                value={parcel?.orderWithAddresses?.order?.orderTime ? formatTimestampToDateTime(parcel?.orderWithAddresses?.order?.orderTime) : '-'}
                            />

                            {/* 发件人（姓名+电话） */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.sender') + ':'}
                                value={(() => {
                                    const name = sender?.fullName || '';
                                    const phone = splicingPhoneInfo(sender);
                                    if (name && phone && phone !== '-') {
                                        return `${name} (${phone})`;
                                    }
                                    return name || phone || '-';
                                })()}
                            />

                            {/* 发件人地址（独占一行） */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.senderAddr') + ':'}
                                value={splicingAddress(sender)}
                                span={2}
                            />

                            {/*退件地址（独占一行） */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.returnAddress') + ':'}
                                value={(() => {
                                    // 优先使用 returnAddress，如果为空则使用 pickupAddress (sender_address_id)
                                    const returnAddr = parcel?.orderWithAddresses?.returnAddress;
                                    if (returnAddr) {
                                        // 使用 OrderOnboardingAddressType 数据
                                        return splicingAddress({
                                            fullName: returnAddr.fullName || '',
                                            phoneCountry: returnAddr.countryCode ? `+${returnAddr.countryCode}` : '+1',
                                            phoneNumber: returnAddr.phoneNumber || '',
                                            phoneExt: '',
                                            addressLine1: returnAddr.addressLine1 || '',
                                            addressLine2: returnAddr.addressLine2 || '',
                                            city: returnAddr.city || '',
                                            state: returnAddr.stateProvince || '',
                                            zipcode: returnAddr.postalCode || '',
                                            country: returnAddr.countryCode || 'US',
                                            geo: ''
                                        });
                                    }
                                    // 回退到 pickupAddress (sender_address_id)
                                    if (parcel?.orderWithAddresses?.pickupAddress) {
                                        const addr = parcel.orderWithAddresses.pickupAddress;
                                        return splicingAddress({
                                            fullName: addr.fullName || '',
                                            phoneCountry: addr.countryCode ? `+${addr.countryCode}` : '+1',
                                            phoneNumber: addr.phoneNumber || '',
                                            phoneExt: addr.extension || '',
                                            addressLine1: addr.addressLine1 || '',
                                            addressLine2: addr.addressLine2 || '',
                                            city: addr.city || '',
                                            state: addr.state || '',
                                            zipcode: addr.zipcode || '',
                                            country: 'US',
                                            geo: ''
                                        });
                                    }
                                    return '-';
                                })()}
                                span={2}
                            />

                            {/* ========== 收件信息模块 ========== */}
                            <SectionTitle title={t('central.parcels.sections.recipientInfo')} />

                            {/* 收件人信息（独占一行） */}
                            <div className="col-span-2">
                                <AddressInfoBlock
                                    value={recipientInfo || undefined}
                                    onChange={setRecipientInfo}
                                    editingBlock={hideEditActions ? 'recipient' : editingBlock}
                                    onEdit={hideEditActions ? undefined : handleEditRecipient}
                                    onEditCancel={() => {
                                        setEditingBlock(null)
                                    }}
                                    originalRecipientInfo={originalRecipientInfo}
                                    onSave={hideEditActions ? undefined : handleUpdateAddress}
                                    onSaveContact={hideEditActions ? undefined : handleUpdateContact}
                                    canEditAddressLocation={parcel?.canEditAddressLocation}
                                    locationPending={buildLocationPendingFromDetail(parcel)}
                                    onSaveLocation={hideEditActions ? undefined : submitLocation}
                                />
                            </div>

                            {/* 原始收件人地址 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.originalRecipientAddr') + ':'}
                                value={(() => {
                                    // 优先使用 originalRecipientAddress，如果为空则使用 deliveryAddress (recipient_address_id)
                                    const originalRecipientAddr = parcel?.orderWithAddresses?.originalRecipientAddress;
                                    if (originalRecipientAddr) {
                                        // 使用 OrderOnboardingAddressType 数据
                                        return splicingAddress({
                                            fullName: originalRecipientAddr.fullName || '',
                                            phoneCountry: originalRecipientAddr.countryCode ? `+${originalRecipientAddr.countryCode}` : '+1',
                                            phoneNumber: originalRecipientAddr.phoneNumber || '',
                                            phoneExt: '',
                                            addressLine1: originalRecipientAddr.addressLine1 || '',
                                            addressLine2: originalRecipientAddr.addressLine2 || '',
                                            city: originalRecipientAddr.city || '',
                                            state: originalRecipientAddr.stateProvince || '',
                                            zipcode: originalRecipientAddr.postalCode || '',
                                            country: originalRecipientAddr.countryCode || 'US',
                                            geo: ''
                                        });
                                    }
                                    // 回退到 deliveryAddress (recipient_address_id)
                                    if (parcel?.orderWithAddresses?.deliveryAddress) {
                                        const addr = parcel.orderWithAddresses.deliveryAddress;
                                        return splicingAddress({
                                            fullName: addr.fullName || '',
                                            phoneCountry: addr.countryCode ? `+${addr.countryCode}` : '+1',
                                            phoneNumber: addr.phoneNumber || '',
                                            phoneExt: addr.extension || '',
                                            addressLine1: addr.addressLine1 || '',
                                            addressLine2: addr.addressLine2 || '',
                                            city: addr.city || '',
                                            state: addr.state || '',
                                            zipcode: addr.zipcode || '',
                                            country: 'US',
                                            geo: splicingGeoInfo(addr.latitude, addr.longitude)
                                        });
                                    }
                                    return '-';
                                })()}
                                span={2}
                            />

                            {/* 原始地址异常提示（仅总部、WMS 入口展示） */}
                            {showInterceptionExemptionAlert && (
                                <VerticalField
                                    label={t('central.parcels.pageDetails.originalAddressExceptionAlerts') + ':'}
                                    value={parcel?.interceptionExemptionAlert
                                        ? t(`central.parcels.pageDetails.interceptionExemptionAlert.${parcel.interceptionExemptionAlert}`, { defaultValue: '-' })
                                        : '-'}
                                    span={2}
                                />
                            )}

                            {/* ========== 物品信息模块 ========== */}
                            <SectionTitle title={t('central.parcels.sections.itemInfo')} />

                            {/* 订单尺寸 */}
                            <VerticalField
                                label={t('delivery.parcels.label.orderDimension') + ':'}
                                value={
                                            [parcel?.orderWithAddresses?.order?.originalLengthCm, parcel?.orderWithAddresses?.order?.originalWidthCm, parcel?.orderWithAddresses?.order?.originalHeightCm].every(v => !v) ?
                                            '-' :
                                            [formatLengthCmToInch(parcel?.orderWithAddresses?.order?.originalLengthCm), formatLengthCmToInch(parcel?.orderWithAddresses?.order?.originalWidthCm), formatLengthCmToInch(parcel?.orderWithAddresses?.order?.originalHeightCm)]
                                            .filter(Boolean)
                                            .join(' * ')
                                }
                            />

                            {/* 订单重量 */}
                            <VerticalField
                                label={t('delivery.parcels.label.orderWeight') + ':'}
                                value={formatWeightKgToLb(parcel?.orderWithAddresses?.order?.originalWeightKg)}
                            />

                            {/* 价值 */}
                            <VerticalField
                                label={t('delivery.parcels.label.orderValue') + ':'}
                                value={parcelInfo?.packageValueUsd}
                            />

                            {/* 实际尺寸 - 可编辑 */}
                            <div className="col-span-2">
                                <ActualDimensionEditor
                                    value={{
                                        lengthCm: parcelInfo?.lengthCm || null,
                                        widthCm: parcelInfo?.widthCm || null,
                                        heightCm: parcelInfo?.heightCm || null,
                                    }}
                                    onChange={(val: ActualDimensionInfo) => {
                                        setParcelInfo({
                                            lengthCm: val.lengthCm,
                                            widthCm: val.widthCm,
                                            heightCm: val.heightCm,
                                            packageValueUsd: parcelInfo?.packageValueUsd ?? null,
                                            volumetricWeight: parcelInfo?.volumetricWeight ?? null,
                                            weight: parcelInfo?.weight ?? null,
                                        });
                                    }}
                                    editingBlock={editingBlock}
                                    onEdit={() => setEditingBlock('dimension')}
                                    onEditCancel={() => setEditingBlock(null)}
                                    onSave={handleUpdateActualDimension}
                                    disabled={embeddedReadOnly || !hasActualDimensionEditPermission() || (parcel?.orderWithAddresses?.order?.isFinished ?? false)}
                                />
                            </div>

                            {/* 实际重量 */}
                            <VerticalField
                                label={t('delivery.parcels.label.actWeight') + ':'}
                                value={formatWeightKgToLb(parcelInfo?.weight)}
                            />

                            {/* 实际体积重量 */}
                            <VerticalField
                                label={t('delivery.parcels.label.actVolumnWeight') + ':'}
                                value={formatWeightKgToLb(parcelInfo?.volumetricWeight)}
                            />

                            <VerticalField
                                label={t('central.parcels.pageDetails.temuHazmat') + ':'}
                                value={temuSortingSegments.hazmatSegment ?? '-'}
                            />
                            <VerticalField
                                label={t('central.parcels.pageDetails.temuBusinessType') + ':'}
                                value={temuSortingSegments.temuBusinessType ?? '-'}
                            />

                            {/* ========== 中转派送模块 ========== */}
                            <SectionTitle title={t('central.parcels.sections.deliveryInfo')} />

                            {/* TT分拣码、TT分拣规则（order_wms） */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.ttSortingCode') + ':'}
                                value={parcel?.orderWms?.ttSortingCode ?? '-'}
                            />
                            <VerticalField
                                label={t('central.parcels.pageDetails.sortingRule') + ':'}
                                value={parcel?.orderWms?.sortingRule ?? '-'}
                            />

                            {/*  分拣代码 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.sortingCode') + ':'}
                                value={parcel?.orderWithAddresses?.order?.sortingCode || '-'}
                            />
                            {/* TT业务类型 / 分拣业务段码 / 尾程业务段码（order_wms + order） */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.ttBizType') + ':'}
                                value={parcel?.orderWms?.ttBizType ?? '-'}
                            />
                            <VerticalField
                                label={t('central.parcels.pageDetails.sortingSegmentCode') + ':'}
                                value={parcel?.orderWms?.sortingSegmentCode ?? '-'}
                            />
                            <VerticalField
                                label={t('central.parcels.pageDetails.lastMileSortingCode') + ':'}
                                value={parcel?.orderWms?.lastMileSortingCode ?? '-'}
                            />

                            {/*  分区 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.zone') + ':'}
                                value={zoneValue || '-'}
                            />

                            {/*  新分区 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.newZone') + ':'}
                                value={parcel?.orderWithAddresses?.order?.newZone || '-'}
                            />

                            {/*  线路号（CS角色不显示） */}
                            {!isCSRole && (
                                <VerticalField
                                    label={t('central.parcels.pageDetails.route') + ':'}
                                    value={parcel?.deliveryInfo?.routeName || '-'}
                                />
                            )}

                            {/* 派送点（CS角色不显示） */}
                            {!isCSRole && (
                                <VerticalField
                                    label={t('central.parcels.pageDetails.stop') + ':'}
                                    value={(() => {
                                        const stop = parcel?.deliveryInfo?.stopNumber;
                                        const subStop = parcel?.deliveryInfo?.subStopNumber;
                                        if (stop != null) {
                                            return formatStopNumber(stop, subStop);
                                        }
                                        return '-';
                                    })()}
                                />
                            )}

                            {/* 任务日期（CS角色不显示） */}
                            {!isCSRole && (
                                <VerticalField
                                    label={t('central.parcels.pageDetails.taskDate') + ':'}
                                    value={parcel?.deliveryInfo?.taskDate || '-'}
                                />
                            )}

                            {/*  司机姓名 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.driverName') + ':'}
                                value={parcel?.deliveryInfo?.driverName || '-'}
                            />

                            {/* 司机电话 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.driverPhone') + ':'}
                                value={parcel?.deliveryInfo?.driverPhone || '-'}
                            />

                            {/*  备注（独占一行） */}
                            <div className="col-span-2">
                                <OrderNoteEditor
                                    value={getOrderNoteText(parcel?.orderNote as any)}
                                    onSave={hideEditActions ? undefined : handleUpdateNote}
                                    maxLength={300}
                                    loading={noteLoading}
                                    readOnly={hideEditActions}
                                />
                                </div>

                            {/* 状态 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.status') + ':'}
                                value={
                                    <span className={`inline-block px-3 py-1.5 rounded-md text-sm font-medium ${getStatusBgColor(parcel?.orderWithAddresses?.order?.overallStatus || '')}`}>
                                        {getStatusText(parcel?.orderWithAddresses?.order?.overallStatus || '')}
                                    </span>
                                }
                            />

                            {/*  配送时间 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.deliveredTime') + ':'}
                                value={parcel?.orderWithAddresses?.order?.deliveredAt ? formatTimestampToDateTime(parcel?.orderWithAddresses?.order?.deliveredAt) : '-'}
                            />

                            {/* 尝试次数 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.attempts') + ':'}
                                value={parcel?.orderWithAddresses?.order?.deliveryAttempts || '-'}
                            />

                            {/* 收件人地址 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.recipientAddr') + ':'}
                                value={splicingAddress(recipientInfo || {
                                    fullName: parcel?.orderWithAddresses?.deliveryAddress?.fullName || '',
                                    phoneCountry: parcel?.orderWithAddresses?.deliveryAddress?.countryCode ? `+${parcel.orderWithAddresses.deliveryAddress.countryCode}` : '+1',
                                    phoneNumber: parcel?.orderWithAddresses?.deliveryAddress?.phoneNumber || '',
                                    phoneExt: parcel?.orderWithAddresses?.deliveryAddress?.extension || '',
                                    addressLine1: parcel?.orderWithAddresses?.deliveryAddress?.addressLine1 || '',
                                    addressLine2: parcel?.orderWithAddresses?.deliveryAddress?.addressLine2 || '',
                                    city: parcel?.orderWithAddresses?.deliveryAddress?.city || '',
                                    state: parcel?.orderWithAddresses?.deliveryAddress?.state || '',
                                    zipcode: parcel?.orderWithAddresses?.deliveryAddress?.zipcode || '',
                                    country: 'US',
                                    geo: splicingGeoInfo(
                                        parcel?.orderWithAddresses?.deliveryAddress?.latitude,
                                        parcel?.orderWithAddresses?.deliveryAddress?.longitude
                                    )
                                })}
                                span={2}
                            />

                            {/*  经纬度 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.geoCoding') + ':'}
                                value={splicingGeoInfo(
                                    parcel?.orderWithAddresses?.deliveryAddress?.latitude,
                                    parcel?.orderWithAddresses?.deliveryAddress?.longitude
                                )}
                                span={2}
                            />

                            {/* 签收位置 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.signingLocation') + ':'}
                                value={(() => {
                                    const lat = parcel?.deliveryInfo?.deliveryLatitude;
                                    const lng = parcel?.deliveryInfo?.deliveryLongitude;
                                    if (lat != null && lng != null) {
                                        return lat === 0 && lng === 0
                                            ? '0,0'
                                            : `${lat.toFixed(7)}, ${lng.toFixed(7)}`;
                                    }
                                    return '-';
                                })()}
                            />

                            {/*  距收件地址 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.distanceFromOrderAddress') + ':'}
                                value={(() => {
                                    const distance = parcel?.deliveryInfo?.deliveryDistance;
                                    const lat = parcel?.deliveryInfo?.deliveryLatitude;
                                    const lng = parcel?.deliveryInfo?.deliveryLongitude;
                                    if (distance != null && lat != null && lng != null && (lat !== 0 || lng !== 0)) {
                                        return `${distance.toFixed(2)} ${t('central.parcels.pageDetails.deliveryDistanceUnit')}`;
                                    }
                                    return '-';
                                })()}
                            />

                            {/* 司机投递备注（独占一行，只读） */}
                            <div className="col-span-2">
                                <div className="mb-4">
                                    <div className="text-gray-500 text-sm mb-1">{t('central.parcels.pageDetails.driverDeliveryNote')}:</div>
                                    <div className="break-words">{(parcel?.driverDeliveryNote && getOrderNoteText(parcel.driverDeliveryNote as any)) || '-'}</div>
                                </div>
                            </div>

                            {/* 门禁信息区域（CS角色不展示） */}
                            <div className="col-span-2 border border-gray-200 rounded-lg overflow-hidden">
                                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                                    <span className="text-sm font-medium text-gray-700">{t('central.parcels.accessCode')}</span>
                                    <div className="flex items-center gap-1">
                                        {!isAccessCodeEditing && (
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={accessInfoExpanded ? <UpOutlined /> : <DownOutlined />}
                                                onClick={() => setAccessInfoExpanded(!accessInfoExpanded)}
                                                className="text-gray-500 hover:text-gray-700"
                                            />
                                        )}
                                        {!embeddedReadOnly && (
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<EditOutlined />}
                                                className="text-gray-500 hover:text-gray-700"
                                                onClick={async () => {
                                                    if (isAccessCodeEditing) {
                                                        try {
                                                            const result = await refetchAccessCodes();
                                                            const list = result.data?.getAccessCodesByOrderId ?? [];
                                                            const newList: AccessCodeItem[] = list.map(item => ({
                                                                id: item.id,
                                                                type: item.codeType,
                                                                value: item.accessCode ?? '',
                                                                status: item.status ?? 0
                                                            }));
                                                            setAccessCodeList(newList);
                                                            setAccessCodeListEditing([]);
                                                        } catch {
                                                            // 如果刷新失败，仅退出编辑模式并保留当前列表
                                                        }
                                                        // 退出编辑时重置门禁码可见性：全部脱敏 & 眼睛图标闭合
                                                        setVisibleAccessCodeIndices(new Set());
                                                    } else {
                                                        setAccessCodeListEditing(accessCodeList.map(x => ({ ...x })));
                                                    }
                                                    setIsAccessCodeEditing(!isAccessCodeEditing);
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="p-3">
                                    {isAccessCodeEditing ? (
                                        <div className="space-y-3">
                                            {(accessCodeListEditing.length ? accessCodeListEditing : accessCodeList).map((item, index) => (
                                                <div key={index} className="flex flex-wrap items-center gap-2 p-3 border border-gray-200 rounded-lg">
                                                    <Select
                                                        className="min-w-[140px]"
                                                        size="small"
                                                        placeholder={t('common.pleaseSelect')}
                                                        value={item.type || undefined}
                                                        options={[...new Set([...Object.values(AccessControlType), ...(accessCodeListEditing.length ? accessCodeListEditing : accessCodeList).map(x => x.type).filter(Boolean)])].map(v => ({ label: t(`enum.accessControlType.${v}`, { defaultValue: v }), value: v }))}
                                                        onChange={(v) => {
                                                            setAccessCodeListEditing(prev => {
                                                                const base = prev.length ? prev : accessCodeList;
                                                                return base.map((x, i) => i === index ? { ...x, type: v ?? x.type } : x);
                                                            });
                                                        }}
                                                    />
                                                    <Input
                                                        size="small"
                                                        className="flex-1 min-w-[120px]"
                                                        placeholder={String(t('central.parcels.accessCodePlaceholder'))}
                                                        value={item.value}
                                                        onChange={(e) => {
                                                            const value = validateAccessCodeValue(e.target.value);
                                                            setAccessCodeListEditing(prev => {
                                                                const base = prev.length ? prev : accessCodeList;
                                                                return base.map((x, i) => i === index ? { ...x, value } : x);
                                                            });
                                                        }}
                                                    />
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={<CloseOutlined />}
                                                        className="text-gray-500 hover:text-red-600"
                                                        onClick={() => {
                                                            const editingList = accessCodeListEditing.length ? accessCodeListEditing : accessCodeList;
                                                            const itemToDelete = editingList[index];
                                                            Modal.confirm({
                                                                title: t('central.parcels.accessCodeDeleteConfirmTitle'),
                                                                content: t('central.parcels.accessCodeDeleteConfirmContent'),
                                                                okText: t('common.yes'),
                                                                cancelText: t('common.no'),
                                                                onOk: async () => {
                                                                    if (itemToDelete?.id != null) {
                                                                        try {
                                                                            await deleteAccessCodeMutation({ variables: { accessCodeId: itemToDelete.id } });
                                                                            message.success(t('central.parcels.accessCodeDeleteSuccess'));
                                                const result = await refetchAccessCodes();
                                                const list = result.data?.getAccessCodesByOrderId ?? [];
                                                const newList: AccessCodeItem[] = list.map(item => ({
                                                    id: item.id,
                                                    type: item.codeType,
                                                    value: item.accessCode ?? '',
                                                    status: item.status ?? 0
                                                }));
                                                setAccessCodeList(newList);
                                                setAccessCodeListEditing(newList.map(x => ({ ...x })));
                                                                        } catch (e) {
                                                                            message.error(t('central.parcels.accessCodeDeleteFailed'));
                                                                            return;
                                                                        }
                                                                    } else {
                                                                        setAccessCodeListEditing(prev => {
                                                                            const base = prev.length ? prev : accessCodeList;
                                                                            return base.filter((_, i) => i !== index);
                                                                        });
                                                                        if (index < accessCodeList.length) {
                                                                            setAccessCodeList(prev => prev.filter((_, i) => i !== index));
                                                                        }
                                                                    }
                                                                }
                                                            });
                                                        }}
                                                    />
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={<CheckOutlined />}
                                                        className="text-gray-500 hover:text-green-600"
                                                        onClick={async () => {
                                                            const editingList = accessCodeListEditing.length ? accessCodeListEditing : accessCodeList;
                                                            const itemToSave = editingList[index];
                                                            if (!itemToSave) return;
                                                            if (!itemToSave.type?.trim()) {
                                                                message.warning(t('central.parcels.accessCodeTypeRequired'));
                                                                return;
                                                            }
                                                            if (!itemToSave.value?.trim()) {
                                                                message.warning(t('central.parcels.accessCodeValueRequired'));
                                                                return;
                                                            }
                                                            try {
                                                                if (!orderIdNum || Number.isNaN(orderIdNum)) {
                                                                    message.error(t('central.parcels.accessCodeSaveFailed'));
                                                                    return;
                                                                }
                                                                if (itemToSave.id != null) {
                                                                    // 更新已有门禁码
                                                                    const { data } = await updateAccessCodeMutation({
                                                                        variables: {
                                                                            input: {
                                                                                orderId: orderIdNum,
                                                                                accessCode: {
                                                                                    id: itemToSave.id,
                                                                                    codeType: itemToSave.type,
                                                                                    accessCode: itemToSave.value,
                                                                                    status: itemToSave.status ?? 0
                                                                                },
                                                                                latitude: null,
                                                                                longitude: null
                                                                            }
                                                                        }
                                                                    });
                                                                    if (!data?.pcUpdateAccessCode.success) {
                                                                        if(data?.pcUpdateAccessCode.resultType == 'duplicate_handled'){
                                                                            message.error(t('central.parcels.accessCodeDuplicateInSameType'));
                                                                        }else{
                                                                            message.error(t('central.parcels.accessCodeSaveFailed'));
                                                                        }

                                                                        return;
                                                                    }
                                                                } else {
                                                                    // 创建新门禁码
                                                                    try {
                                                                        const { data } = await createAccessCodeMutation({
                                                                            variables: {
                                                                                input: {
                                                                                    orderId: orderIdNum,
                                                                                    codeType: itemToSave.type,
                                                                                    accessCode: itemToSave.value,
                                                                                    latitude: null,
                                                                                    longitude: null
                                                                                }
                                                                            }
                                                                        });
                                                                        if (!data?.pcCreateAccessCode.success) {
                                                                            if (data?.pcCreateAccessCode.resultType == 'duplicate_handled') {
                                                                                message.error(t('central.parcels.accessCodeDuplicateInSameType'));
                                                                            } else {
                                                                                message.error(t('central.parcels.accessCodeSaveFailed'));
                                                                            }
                                                                            return;
                                                                        }
                                                                    } catch (error: any) {
                                                                        const graphQLError = error?.graphQLErrors?.[0];
                                                                        const messageKey = graphQLError?.extensions?.messageKey as string | undefined;
                                                                        if (messageKey === 'errors.business.accessCode.createFailed') {
                                                                            // 门禁码创建失败，地址无法解析，无法创建门禁码
                                                                            message.error(t('errors.business.accessCode.createFailed'));
                                                                        } else {
                                                                            message.error(t('central.parcels.accessCodeSaveFailed'));
                                                                        }
                                                                        return;
                                                                    }
                                                                }
                                                                const result = await refetchAccessCodes();
                                                                const list = result.data?.getAccessCodesByOrderId ?? [];
                                                                const newList: AccessCodeItem[] = list.map(item => ({
                                                                    id: item.id,
                                                                    type: item.codeType,
                                                                    value: item.accessCode ?? '',
                                                                    status: item.status ?? 0
                                                                }));
                                                                setAccessCodeList(newList);
                                                                setAccessCodeListEditing(newList.map(x => ({ ...x })));
                                                                message.success(t('common.messages.saved'));
                                                            } catch (e) {
                                                                message.error(t('central.parcels.accessCodeSaveFailed'));
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                            <Button
                                                type="dashed"
                                                size="small"
                                                className="mt-2"
                                                onClick={() => {
                                                    setAccessCodeListEditing(prev => {
                                                        const base = prev.length ? prev : accessCodeList;
                                                        return [...base, { type: '', value: '', status: 0 } as AccessCodeItem];
                                                    });
                                                }}
                                            >
                                                {t('central.parcels.accessCodeAdd')}
                                            </Button>
                                        </div>
                                    ) : (
                                        accessCodeList.length === 0 ? (
                                            <span className="text-sm text-gray-500">-</span>
                                        ) : (
                                            <Row gutter={[16, 12]}>
                                                {accessCodeList.slice(0, 4).map((item, idx) => (
                                                    <Col key={idx} span={12}>
                                                        <div className="text-sm flex items-center gap-1">
                                                            <span className="text-gray-500">{t(`enum.accessControlType.${item.type}`, { defaultValue: item.type })}</span>
                                                            <span className="text-gray-500"> : </span>
                                                            <span className="text-gray-800">
                                                                {visibleAccessCodeIndices.has(idx)
                                                                    ? (item.value?.trim() ? item.value : '-')
                                                                    : '***'}
                                                            </span>
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={visibleAccessCodeIndices.has(idx) ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                                                className="text-gray-500 hover:text-gray-700 p-0 h-auto"
                                                                onClick={async () => {
                                                                    if (!visibleAccessCodeIndices.has(idx) && item.id != null) {
                                                                        try {
                                                                            await savePcAccessCodeQueryLog({
                                                                                variables: { accessCodeId: item.id }
                                                                            });
                                                                        } catch {
                                                                            // 日志失败不影响显示
                                                                        }
                                                                    }
                                                                    setVisibleAccessCodeIndices(prev => {
                                                                        const next = new Set(prev);
                                                                        if (next.has(idx)) next.delete(idx);
                                                                        else next.add(idx);
                                                                        return next;
                                                                    });
                                                                }}
                                                            />
                                                        </div>
                                                    </Col>
                                                ))}
                                                {accessInfoExpanded && accessCodeList.slice(4).map((item, idx) => {
                                                    const realIdx = idx + 4;
                                                    return (
                                                        <Col key={realIdx} span={12}>
                                                            <div className="text-sm flex items-center gap-1">
                                                                <span className="text-gray-500">{t(`enum.accessControlType.${item.type}`, { defaultValue: item.type })}</span>
                                                                <span className="text-gray-500"> : </span>
                                                                <span className="text-gray-800">
                                                                    {visibleAccessCodeIndices.has(realIdx)
                                                                        ? (item.value?.trim() ? item.value : '-')
                                                                        : '***'}
                                                                </span>
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={visibleAccessCodeIndices.has(realIdx) ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                                                    className="text-gray-500 hover:text-gray-700 p-0 h-auto"
                                                                    onClick={async () => {
                                                                        if (!visibleAccessCodeIndices.has(realIdx) && item.id != null) {
                                                                            try {
                                                                                await savePcAccessCodeQueryLog({
                                                                                    variables: { accessCodeId: item.id }
                                                                                });
                                                                            } catch {
                                                                                // 日志失败不影响显示
                                                                            }
                                                                        }
                                                                        setVisibleAccessCodeIndices(prev => {
                                                                            const next = new Set(prev);
                                                                            if (next.has(realIdx)) next.delete(realIdx);
                                                                            else next.add(realIdx);
                                                                            return next;
                                                                        });
                                                                    }}
                                                                />
                                                            </div>
                                                        </Col>
                                                    );
                                                })}
                                            </Row>
                                        )
                                    )}
                                </div>
                            </div>

                            {/* POD审核状态 */}
                            <VerticalField
                                label={t('delivery.parcels.label.podReviewStatus') + ':'}
                                value={
                                    <span className={`inline-block px-3 py-1.5 rounded-md text-sm font-medium ${
                                        parcel?.orderWithAddresses?.order?.podReviewStatus === PODReviewStatus.MANUAL_APPROVED ||
                                        parcel?.orderWithAddresses?.order?.podReviewStatus === PODReviewStatus.MACHINE_APPROVED
                                            ? 'bg-green-50 text-green-600 border border-green-200'
                                            : parcel?.orderWithAddresses?.order?.podReviewStatus === PODReviewStatus.MANUAL_DISAPPROVED ||
                                              parcel?.orderWithAddresses?.order?.podReviewStatus === PODReviewStatus.MACHINE_DISAPPROVED
                                            ? 'bg-red-50 text-red-600 border border-red-200'
                                            : parcel?.orderWithAddresses?.order?.podReviewStatus === PODReviewStatus.MANUAL_PENDING
                                            ? 'bg-yellow-50 text-yellow-600 border border-yellow-200'
                                            : 'bg-gray-50 text-gray-600 border border-gray-200'
                                    }`}>
                                            {getPodReviewStatusText(parcel?.orderWithAddresses?.order?.podReviewStatus || PODReviewStatus.NOT_REVIEWED, t)}
                                    </span>
                                }
                            />

                            {/* 审核评论 */}
                            <VerticalField
                                label={t('podReview.detail.reviewComment') + ':'}
                                value={formatPodReviewMessage(parcel?.orderWithAddresses?.order?.podReviewMessage, t) || '—'}
                                span={2}
                            />

                            {/* POD图片（独占一行） */}
                            <div className="col-span-2">
                                <div className="text-gray-500 text-sm mb-2">{t('central.parcels.pageDetails.podPhoto')}</div>
                                            {hasPodUpdatePermission && (parcel?.orderWithAddresses?.order?.overallStatus === 'DELIVERED' || parcel?.orderWithAddresses?.order?.overallStatus === 'PRE_DELIVERED') ? (
                                                <div>
                                                    <ImageUploader
                                                        value={podPhotoObjectKeys}
                                                        displayUrls={displayPodPhotos}
                                                        onChange={handlePodPhotosChange}
                                                        maxCount={6}
                                                        tag="pod"
                                                    />
                                                    {podPhotosChanged && (
                                                        <div className="mt-2">
                                                            <Button
                                                                type="primary"
                                                                onClick={handleSubmitPodPhotos}
                                                                loading={submittingPodPhotos}
                                                            >
                                                                {t('central.parcels.pageDetails.submitChanges')}
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="flex space-x-2">
                                                    {parcel?.orderWithAddresses?.order?.podImageS3UrlsJson.length > 0 ? (
                                                        parcel?.orderWithAddresses?.order?.podImageS3UrlsJson.map((photo: any, index: number) => (
                                                            <div key={index} className="w-20 h-20 border border-gray-300 rounded overflow-hidden">
                                                                <Image
                                                                    src={photo}
                                                                    alt={`POD ${index + 1}`}
                                                                    width={80}
                                                                    height={80}
                                                                    className="object-cover"
                                                                />
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <>
                                                            <div className="w-20 h-20 border border-gray-300 rounded flex items-center justify-center">
                                                                <CameraOutlined className="text-2xl text-gray-400"/>
                                                            </div>
                                                            <div className="w-20 h-20 border border-gray-300 rounded flex items-center justify-center">
                                                                <CameraOutlined className="text-2xl text-gray-400"/>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                </div>

                            {/* ========== 异常信息模块 ========== */}
                            <SectionTitle title={t('central.parcels.sections.exceptionInfo')} />

                            {/* 投诉 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.complaint') + ':'}
                                value={getComplaintText(parcel?.orderWithAddresses?.order?.isComplaint === true)}
                            />

                            {/* 异常：有一级显示一级，有二级原因时同时显示一级和二级 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.exception') + ':'}
                                value={exception ? getExceptionReasonDisplayText(exception, parcel?.orderWithAddresses?.order?.exceptionSubReasons) : '-'}
                            />

                            {/* 下一步动作 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.nextAction') + ':'}
                                value={getNextActionText(parcel?.orderWithAddresses?.order?.nextAction || '')}
                            />

                            {/* 异常状态 */}
                            <VerticalField
                                label={t('central.parcels.pageDetails.exceptionStatus') + ':'}
                                value={getExceptionStatusText(parcel?.orderWithAddresses?.order?.isHasBeenSetException || false, parcel?.orderWithAddresses?.order?.isAbnormal || false, parcel?.orderWithAddresses?.order?.exceptionStatus)}
                            />

                            {/* 异常图片 */}
                            <div>
                                <div className="text-gray-500 text-sm mb-2">{t('central.parcels.pageDetails.exceptionPhoto') + ':'}</div>
                                <div className="flex space-x-2">
                                    {parcel?.orderWithAddresses?.order?.exceptionImageS3UrlsJson?.length > 0 ? (
                                        parcel.orderWithAddresses.order.exceptionImageS3UrlsJson.map((photo: any, index: number) => (
                                            <div key={index} className="w-20 h-20 border border-gray-300 rounded overflow-hidden">
                                                <Image
                                                    src={photo}
                                                    alt={`Exception ${index + 1}`}
                                                    width={80}
                                                    height={80}
                                                    className="object-cover"
                                                />
                                            </div>
                                        ))
                                    ) : (
                                        <>
                                            <div className="w-20 h-20 border border-gray-300 rounded flex items-center justify-center">
                                                <CameraOutlined className="text-2xl text-gray-400"/>
                                            </div>
                                            <div className="w-20 h-20 border border-gray-300 rounded flex items-center justify-center">
                                                <CameraOutlined className="text-2xl text-gray-400"/>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* 库位号和存储编号（仅hold状态展示，与异常图片同行右侧） */}
                            {parcel?.orderWithAddresses?.order?.overallStatus === 'HOLDING' ? (
                                <div className="space-y-2">
                                    <VerticalField
                                        label={t('central.parcels.pageDetails.warehouseLocationCode') + ':'}
                                        value={parcel?.orderWithAddresses?.order?.warehouseLocationCode || '-'}
                                    />
                                    <VerticalField
                                        label={t('central.parcels.pageDetails.storageNumber') + ':'}
                                        value={parcel?.orderWithAddresses?.order?.storageNumber != null ? String(parcel.orderWithAddresses.order.storageNumber).padStart(4, '0') : '-'}
                                    />
                                </div>
                            ) : <div />}

                            {/* 最后扫码司机（CS角色不显示） */}
                            {!isCSRole && (
                                <VerticalField
                                    label={t('central.parcels.pageDetails.lastScannedDriver') + ':'}
                                    value={parcel?.deliveryInfo?.lastScannedDriverName || '-'}
                                />
                            )}

                            {/* 最后指派司机（CS角色不显示） */}
                            {!isCSRole && (
                                <VerticalField
                                    label={t('central.parcels.pageDetails.lastAssignedDriver') + ':'}
                                    value={parcel?.deliveryInfo?.lastAssignedDriverName || '-'}
                                />
                            )}

                            {/* 操作按钮（独占一行） */}
                            {!isCSRole && (
                                <div className="col-span-2 flex gap-2 mt-4">
                                    {!hideEditActions && !shouldHidePrintAndExceptionButtons && !hidePrintAndPodReview && (
                                        <>
                                            <Button 
                                                type="primary" 
                                                onClick={handlePrintLabel}
                                            >
                                                {t('central.parcels.pageDetails.printLabel')}
                                            </Button>
                                            <Button 
                                                type="primary" 
                                                onClick={handleMarkAsException}
                                            >
                                                {t('central.parcels.pageDetails.exception')}
                                            </Button>
                                        </>
                                    )}
                                    {!hideEditActions && !shouldHidePrintAndExceptionButtons && hidePrintAndPodReview && (
                                        <Button 
                                            type="primary" 
                                            onClick={handleMarkAsException}
                                        >
                                            {t('central.parcels.pageDetails.exception')}
                                        </Button>
                                    )}
                                    {!hidePrintAndPodReview && (
                                        <Button
                                            type="primary"
                                            onClick={handleAddToPodReview}
                                            loading={addToPodReviewLoading}
                                        >
                                            {t('podReview.addToPodReview.title')}
                                        </Button>
                                    )}
                                    {extraActionButtons}
                                    {(() => {
                                        const hasRevokeResource = hasResource([
                                            ResourceCode.menu.HUB_PACKAGE_DETAIL,
                                            ResourceCode.menu.WHS_PACKAGE_DETAIL,
                                            ResourceCode.menu.HUB_EXCEPTION_DETAIL,
                                            ResourceCode.menu.WHS_EXCEPTION_DETAIL,
                                            ResourceCode.menu.HUB_LABEL_SEARCH_DETAIL,
                                            ResourceCode.menu.WHS_LABEL_SEARCH_DETAIL,
                                        ]);
                                        const revokeStatus = parcel?.deliveryRevokeStatus;
                                        const canRevoke = !embeddedReadOnly
                                            && (revokeStatus?.canRevoke ?? false)
                                            && (userRole === AppRoleConstants.DSP_MANAGER || hasRevokeResource);
                                        return canRevoke ? (
                                            <Button
                                                type="primary"
                                                onClick={() => setShowRevokeModal(true)}
                                            >
                                                {t('central.parcels.pageDetails.revoke')}
                                            </Button>
                                        ) : null;
                                    })()}
                                </div>
                            )}

                            {/* 撤回 Modal */}
                            <Modal
                                title={
                                    <div className="flex items-center gap-2">
                                        <div style={{ width: 4, height: 18, backgroundColor: '#722ed1', borderRadius: 2 }} />
                                        <span>{t('central.parcels.pageDetails.revokeTitle')}</span>
                                    </div>
                                }
                                open={showRevokeModal}
                                onCancel={() => { setShowRevokeModal(false); setSelectedRevokeReason(''); }}
                                onOk={async () => {
                                    const trackingNumber = parcel?.orderWithAddresses?.order?.trackingNumber;
                                    if (!trackingNumber || !selectedRevokeReason) return;
                                    try {
                                        await revokeDelivery({ variables: { trackingNumber, revokeReason: selectedRevokeReason } });
                                        message.success(t('central.parcels.pageDetails.revokeSuccess'));
                                        setShowRevokeModal(false);
                                        setSelectedRevokeReason('');
                                        refetch();
                                    } catch (e) {
                                        const { messageKey } = getErrorKeys(e);
                                        const NON_REVOKABLE_MESSAGE_KEYS = [
                                            'errors.business.order.alreadyFinished',
                                            'errors.business.order.intercepted',
                                            'errors.business.order.notExist',
                                            'errors.business.delivery.revoke.notRevokable',
                                            'errors.business.delivery.revoke.windowExpired',
                                            'errors.business.delivery.revoke.alreadyRevoked',
                                        ];
                                        if (messageKey && NON_REVOKABLE_MESSAGE_KEYS.includes(messageKey)) {
                                            message.warning(t('central.parcels.pageDetails.revokeWindowExpired'));
                                            setShowRevokeModal(false);
                                            setSelectedRevokeReason('');
                                            refetch();
                                        } else {
                                            message.error(messageKey ? t(messageKey) : t('central.parcels.pageDetails.revokeFailed'));
                                        }
                                    }
                                }}
                                okText={t('central.parcels.pageDetails.revokeConfirm')}
                                cancelText={t('central.parcels.pageDetails.revokeCancel')}
                                okButtonProps={{ loading: revokeLoading, disabled: !selectedRevokeReason, style: { backgroundColor: '#722ed1', borderColor: '#722ed1', color: 'white' } }}
                            >
                                <p className="font-semibold mb-4">
                                    {t('central.parcels.pageDetails.revokeWarningPrefix')}
                                    <span className="text-red-600">{t('central.parcels.pageDetails.revokeWarningHighlight1')}</span>
                                    {t('central.parcels.pageDetails.revokeWarningMiddle')}
                                    <span className="text-red-600">{t('central.parcels.pageDetails.revokeWarningHighlight2')}</span>
                                    {t('central.parcels.pageDetails.revokeWarningSuffix')}
                                </p>
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-red-500">*</span>
                                    <span>{t('central.parcels.pageDetails.revokeReasonLabel')}</span>
                                </div>
                                <Select
                                    style={{ width: '100%' }}
                                    placeholder={t('central.parcels.pageDetails.revokeReasonPlaceholder')}
                                    value={selectedRevokeReason || undefined}
                                    onChange={(val) => setSelectedRevokeReason(val)}
                                    options={revokeReasons.map((item) => ({
                                        label: i18n.language.startsWith('zh') ? item.zh : i18n.language.startsWith('es') ? item.es : item.en,
                                        value: item.code,
                                    }))}
                                />
                            </Modal>

                            {/* 异常Modal */}
                                {markAsExceptionVisible && parcel?.orderWithAddresses?.order && (
                                    <MarkAsExceptionModal
                                        visible={markAsExceptionVisible}
                                        onClose={() => setMarkAsExceptionVisible(false)}
                                        onConfirm={handleMarkAsExceptionConfirm}
                                        loading={markAsExceptionLoading}
                                        parcel={parcel.orderWithAddresses.order}
                                        userRole={userRole}
                                    />
                                )}
                            </div>
                                </div>
                    <div className="w-2/5 overflow-y-auto pl-4 border-l flex-shrink-0">
                        <ParcelHistorySection
                            parcel={parcel}
                            channelName={channelName}
                            showExternalTrack={showExternalTrack}
                            t={t}
                        />
                    </div>
                </div>
            )}
            {/* antd 静态弹窗（Modal.error）z-index 为 zIndexPopupBase(1000)+CONTAINER_MAX_OFFSET(1000)=2000，指引弹层需更高才不被遮挡 */}
            <AddressGuideModal open={addressGuideOpen} onClose={() => setAddressGuideOpen(false)} zIndex={2050} />
        </Drawer>
    );
};

export default CentralParcelDetailDrawer;